import React, { useRef, useEffect } from "react";
import "./index.scss";
import { loadScript } from "./load";

const VERSION = "0.34.2";
const SDK_URL = `https://sdk.mvp.fan/web-sdk/${VERSION}/index.js`;
const API_KEY = process.env.WSC_BLAZE_API_KEY || "";

declare global {
  interface Window {
    BlazeSDK: any;
    BlazeSDKInitialized: boolean;
  }
}

let sdkPromise: Promise<any> | null = null;

const getBlazeSDK = (): Promise<any> => {
  const sdk = window.BlazeSDK;

  if (
    sdk &&
    sdk.isInitialized &&
    sdk.isInitialized() &&
    window.BlazeSDKInitialized
  ) {
    sdkPromise = Promise.resolve(sdk);
    return sdkPromise;
  }

  if (sdkPromise) return sdkPromise;

  sdkPromise = loadScript(SDK_URL)
    .then(
      () =>
        new Promise<any>(resolve => {
          const { BlazeSDK } = window;

          if (!window.BlazeSDKInitialized) {
            BlazeSDK.Initialize(API_KEY);
            window.BlazeSDKInitialized = true;
          }

          if (BlazeSDK.isInitialized && BlazeSDK.isInitialized()) {
            resolve(BlazeSDK);
            return;
          }

          document.addEventListener(
            "onBlazeSDKConnect",
            () => resolve(window.BlazeSDK),
            { once: true }
          );
        })
    )
    .catch(err => {
      sdkPromise = null;
      throw err;
    });

  return sdkPromise;
};

type ContentType = "story" | "moment" | "video";
type WidgetLayoutType =
  | "row-circle"
  | "row-rectangle"
  | "grid-2-columns"
  | "grid-3-columns"
  | "row-rectangle-horizontal"
  | "row-rectangle-animated"
  | "grid-2-columns-horizontal"
  | "grid-3-columns-horizontal"
  | "default";

type Props = {
  contentType?: ContentType;
  widgetType?: WidgetLayoutType;
  label: string;
};

const WSCBlazeSDK = ({
  contentType = "story",
  widgetType = "row-rectangle",
  label
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const labelFormatted = label.trim();
  const labels = labelFormatted.includes(",")
    ? labelFormatted.split(",")
    : [labelFormatted];
  const id = `wsc-blaze-${contentType}-${widgetType}-${label.replace(
    /,/g,
    "-"
  )}`;

  useEffect(() => {
    let mounted = true;

    getBlazeSDK().then(BlazeSDK => {
      if (!mounted || !containerRef.current) return;

      const theme = BlazeSDK.Theme(widgetType, contentType);
      const isTour = document.querySelector(
        ".itf-tour + .page-section:has(.wsc-blaze)"
      );

      isTour && (theme.layoutStyle.labelStyle.color = "white");

      BlazeSDK.addDelegateListener(
        BlazeSDK.Delegations.onEventTriggered,
        () => {}
      );

      const [first, ...rest] = labels;

      const singleLabel = BlazeSDK.LabelBuilder().singleLabel(first);

      const dataSource = BlazeSDK.DataSourceBuilder().labels({
        labels: rest.length
          ? BlazeSDK.LabelBuilder().mustInclude(first, ...rest)
          : singleLabel,
        maxItems: 20
      });

      widgetRef.current = BlazeSDK.WidgetGridView(id, {
        dataSource,
        theme,
        contentType,
        delegates: {
          [BlazeSDK.Delegations.onWidgetDataLoadCompleted]: (e: any) => {
            if (e.detail.contentCount === 0) {
              const el = document.querySelector<HTMLElement>(`#${id}`);
              if (el) el.style.display = "none";
            }
          }
        }
      });
    });

    return () => {
      mounted = false;
      if (widgetRef.current && widgetRef.current.destroy) {
        widgetRef.current.destroy();
      }
    };
  }, []);

  return (
    <div
      id={id}
      ref={containerRef}
      className="wsc-blaze"
      style={{ minHeight: widgetType === "row-circle" ? 155 : 300 }}
    />
  );
};

export default WSCBlazeSDK;
